/**
 * LaTeX symbols: command, Unicode character, the package that defines the command, and
 * a category. Curated from the LaTeX kernel, amsmath, amssymb/amsfonts, textcomp and
 * the T1 font encoding, in the spirit of the Comprehensive LaTeX Symbol List; math-mode
 * characters are checked against KaTeX's own command table in the tests.
 *
 * A symbol defined by several packages lists the one most documents already load:
 * `\lhd` is in latexsym too, but amssymb is the usual source.
 */

export const SYMBOL_CATEGORIES = [
  "greek",
  "binary",
  "relation",
  "negated",
  "arrow",
  "logic",
  "big",
  "delimiter",
  "accent",
  "dots",
  "misc",
  "letters",
  "text",
  "textAccent",
] as const;

export type SymbolCategory = (typeof SYMBOL_CATEGORIES)[number];

export const SYMBOL_PACKAGES = [
  "latex",
  "amsmath",
  "amssymb",
  "mathtools",
  "textcomp",
  "fontenc",
  "stmaryrd",
  "esint",
  "mathdots",
  "mathrsfs",
  "gensymb",
] as const;

export type SymbolPackage = (typeof SYMBOL_PACKAGES)[number];

export interface LatexSymbol {
  command: string;
  /** The Unicode character(s) the command produces; accents use an example letter. */
  char: string;
  /** Plain English name, used for search. */
  name: string;
  category: SymbolCategory;
  package: SymbolPackage;
  mode: "math" | "text";
}

type Row = readonly [
  command: string,
  char: string,
  name: string,
  pkg?: SymbolPackage,
];

function group(
  category: SymbolCategory,
  mode: "math" | "text",
  defaultPackage: SymbolPackage,
  rows: readonly Row[]
): LatexSymbol[] {
  return rows.map(([command, char, name, pkg]) => ({
    command,
    char,
    name,
    category,
    package: pkg ?? defaultPackage,
    mode,
  }));
}

const GREEK: readonly Row[] = [
  ["\\alpha", "α", "alpha"],
  ["\\beta", "β", "beta"],
  ["\\gamma", "γ", "gamma"],
  ["\\delta", "δ", "delta"],
  ["\\epsilon", "ϵ", "epsilon (lunate)"],
  ["\\varepsilon", "ε", "epsilon (curly variant)"],
  ["\\zeta", "ζ", "zeta"],
  ["\\eta", "η", "eta"],
  ["\\theta", "θ", "theta"],
  ["\\vartheta", "ϑ", "theta (script variant)"],
  ["\\iota", "ι", "iota"],
  ["\\kappa", "κ", "kappa"],
  ["\\varkappa", "ϰ", "kappa (variant)", "amssymb"],
  ["\\lambda", "λ", "lambda"],
  ["\\mu", "μ", "mu"],
  ["\\nu", "ν", "nu"],
  ["\\xi", "ξ", "xi"],
  ["\\pi", "π", "pi"],
  ["\\varpi", "ϖ", "pi (variant, omega-like)"],
  ["\\rho", "ρ", "rho"],
  ["\\varrho", "ϱ", "rho (variant)"],
  ["\\sigma", "σ", "sigma"],
  ["\\varsigma", "ς", "sigma (final form)"],
  ["\\tau", "τ", "tau"],
  ["\\upsilon", "υ", "upsilon"],
  ["\\phi", "ϕ", "phi (straight)"],
  ["\\varphi", "φ", "phi (loopy variant)"],
  ["\\chi", "χ", "chi"],
  ["\\psi", "ψ", "psi"],
  ["\\omega", "ω", "omega"],
  ["\\digamma", "ϝ", "digamma", "amssymb"],
  ["\\Gamma", "Γ", "capital gamma"],
  ["\\Delta", "Δ", "capital delta"],
  ["\\Theta", "Θ", "capital theta"],
  ["\\Lambda", "Λ", "capital lambda"],
  ["\\Xi", "Ξ", "capital xi"],
  ["\\Pi", "Π", "capital pi"],
  ["\\Sigma", "Σ", "capital sigma"],
  ["\\Upsilon", "Υ", "capital upsilon"],
  ["\\Phi", "Φ", "capital phi"],
  ["\\Psi", "Ψ", "capital psi"],
  ["\\Omega", "Ω", "capital omega"],
  ["\\varGamma", "𝛤", "italic capital gamma", "amsmath"],
  ["\\varDelta", "𝛥", "italic capital delta", "amsmath"],
  ["\\varTheta", "𝛩", "italic capital theta", "amsmath"],
  ["\\varLambda", "𝛬", "italic capital lambda", "amsmath"],
  ["\\varXi", "𝛯", "italic capital xi", "amsmath"],
  ["\\varPi", "𝛱", "italic capital pi", "amsmath"],
  ["\\varSigma", "𝛴", "italic capital sigma", "amsmath"],
  ["\\varUpsilon", "𝛶", "italic capital upsilon", "amsmath"],
  ["\\varPhi", "𝛷", "italic capital phi", "amsmath"],
  ["\\varPsi", "𝛹", "italic capital psi", "amsmath"],
  ["\\varOmega", "𝛺", "italic capital omega", "amsmath"],
];

const BINARY: readonly Row[] = [
  ["\\pm", "±", "plus-minus"],
  ["\\mp", "∓", "minus-plus"],
  ["\\times", "×", "times, multiplication, cross product"],
  ["\\div", "÷", "division sign, obelus"],
  ["\\cdot", "⋅", "centered dot, dot product"],
  ["\\ast", "∗", "asterisk operator, convolution"],
  ["\\star", "⋆", "star operator"],
  ["\\circ", "∘", "ring operator, composition"],
  ["\\bullet", "∙", "bullet operator"],
  ["\\cap", "∩", "intersection"],
  ["\\cup", "∪", "union"],
  ["\\uplus", "⊎", "multiset union"],
  ["\\sqcap", "⊓", "square cap, meet"],
  ["\\sqcup", "⊔", "square cup, join"],
  ["\\wedge", "∧", "logical and, wedge"],
  ["\\vee", "∨", "logical or, vee"],
  ["\\setminus", "∖", "set minus, set difference"],
  ["\\smallsetminus", "∖", "small set minus", "amssymb"],
  ["\\wr", "≀", "wreath product"],
  ["\\diamond", "⋄", "diamond operator"],
  ["\\bigtriangleup", "△", "big triangle up"],
  ["\\bigtriangledown", "▽", "big triangle down"],
  ["\\triangleleft", "◃", "triangle left"],
  ["\\triangleright", "▹", "triangle right"],
  ["\\lhd", "⊲", "left normal subgroup triangle", "amssymb"],
  ["\\rhd", "⊳", "right normal subgroup triangle", "amssymb"],
  ["\\unlhd", "⊴", "left triangle with underline", "amssymb"],
  ["\\unrhd", "⊵", "right triangle with underline", "amssymb"],
  ["\\oplus", "⊕", "circled plus, direct sum, xor"],
  ["\\ominus", "⊖", "circled minus"],
  ["\\otimes", "⊗", "circled times, tensor product"],
  ["\\oslash", "⊘", "circled slash"],
  ["\\odot", "⊙", "circled dot"],
  ["\\bigcirc", "◯", "large circle"],
  ["\\circledast", "⊛", "circled asterisk", "amssymb"],
  ["\\circledcirc", "⊚", "circled ring", "amssymb"],
  ["\\circleddash", "⊝", "circled dash", "amssymb"],
  ["\\boxplus", "⊞", "squared plus", "amssymb"],
  ["\\boxminus", "⊟", "squared minus", "amssymb"],
  ["\\boxtimes", "⊠", "squared times", "amssymb"],
  ["\\boxdot", "⊡", "squared dot", "amssymb"],
  ["\\dagger", "†", "dagger"],
  ["\\ddagger", "‡", "double dagger"],
  ["\\amalg", "⨿", "amalgamation, coproduct"],
  ["\\dotplus", "∔", "dot plus", "amssymb"],
  ["\\Cap", "⋒", "double intersection", "amssymb"],
  ["\\Cup", "⋓", "double union", "amssymb"],
  ["\\barwedge", "⊼", "nand, bar wedge", "amssymb"],
  ["\\veebar", "⊻", "xor, vee bar", "amssymb"],
  ["\\doublebarwedge", "⩞", "double bar wedge", "amssymb"],
  ["\\curlywedge", "⋏", "curly logical and", "amssymb"],
  ["\\curlyvee", "⋎", "curly logical or", "amssymb"],
  ["\\ltimes", "⋉", "left semidirect product", "amssymb"],
  ["\\rtimes", "⋊", "right semidirect product", "amssymb"],
  ["\\leftthreetimes", "⋋", "left semidirect product (three times)", "amssymb"],
  [
    "\\rightthreetimes",
    "⋌",
    "right semidirect product (three times)",
    "amssymb",
  ],
  ["\\divideontimes", "⋇", "division times", "amssymb"],
  ["\\intercal", "⊺", "intercalate, transpose", "amssymb"],
  ["\\centerdot", "⋅", "centered dot (binary)", "amssymb"],
];

const RELATION: readonly Row[] = [
  ["\\leq", "≤", "less than or equal to"],
  ["\\le", "≤", "less than or equal to (short form)"],
  ["\\geq", "≥", "greater than or equal to"],
  ["\\ge", "≥", "greater than or equal to (short form)"],
  ["\\equiv", "≡", "identical to, congruent, equivalent"],
  ["\\approx", "≈", "approximately equal to"],
  ["\\cong", "≅", "congruent, isomorphic"],
  ["\\sim", "∼", "similar to, distributed as, tilde"],
  ["\\simeq", "≃", "asymptotically equal to"],
  ["\\propto", "∝", "proportional to"],
  ["\\ll", "≪", "much less than"],
  ["\\gg", "≫", "much greater than"],
  ["\\prec", "≺", "precedes"],
  ["\\succ", "≻", "succeeds"],
  ["\\preceq", "⪯", "precedes or equals"],
  ["\\succeq", "⪰", "succeeds or equals"],
  ["\\asymp", "≍", "asymptotic, equivalent to"],
  ["\\doteq", "≐", "approaches the limit, dot equals"],
  ["\\parallel", "∥", "parallel to"],
  ["\\perp", "⊥", "perpendicular, orthogonal"],
  ["\\mid", "∣", "divides, such that"],
  ["\\smile", "⌣", "smile"],
  ["\\frown", "⌢", "frown"],
  ["\\bowtie", "⋈", "bowtie, natural join"],
  ["\\Join", "⋈", "join", "amssymb"],
  ["\\models", "⊨", "models, satisfies"],
  ["\\vdash", "⊢", "proves, turnstile"],
  ["\\dashv", "⊣", "left tack"],
  ["\\sqsubset", "⊏", "square subset", "amssymb"],
  ["\\sqsupset", "⊐", "square superset", "amssymb"],
  ["\\sqsubseteq", "⊑", "square subset or equal"],
  ["\\sqsupseteq", "⊒", "square superset or equal"],
  ["\\leqq", "≦", "less than over equal", "amssymb"],
  ["\\geqq", "≧", "greater than over equal", "amssymb"],
  ["\\leqslant", "⩽", "less than or slanted equal", "amssymb"],
  ["\\geqslant", "⩾", "greater than or slanted equal", "amssymb"],
  ["\\eqslantless", "⪕", "slanted equal or less than", "amssymb"],
  ["\\eqslantgtr", "⪖", "slanted equal or greater than", "amssymb"],
  ["\\lesssim", "≲", "less than or equivalent to", "amssymb"],
  ["\\gtrsim", "≳", "greater than or equivalent to", "amssymb"],
  ["\\lessapprox", "⪅", "less than or approximate", "amssymb"],
  ["\\gtrapprox", "⪆", "greater than or approximate", "amssymb"],
  ["\\approxeq", "≊", "almost equal or equal to", "amssymb"],
  ["\\lll", "⋘", "very much less than", "amssymb"],
  ["\\ggg", "⋙", "very much greater than", "amssymb"],
  ["\\lessdot", "⋖", "less than with dot", "amssymb"],
  ["\\gtrdot", "⋗", "greater than with dot", "amssymb"],
  ["\\lessgtr", "≶", "less than or greater than", "amssymb"],
  ["\\gtrless", "≷", "greater than or less than", "amssymb"],
  ["\\lesseqgtr", "⋚", "less than, equal or greater than", "amssymb"],
  ["\\gtreqless", "⋛", "greater than, equal or less than", "amssymb"],
  ["\\lesseqqgtr", "⪋", "less than, double equal, greater than", "amssymb"],
  ["\\gtreqqless", "⪌", "greater than, double equal, less than", "amssymb"],
  ["\\doteqdot", "≑", "geometrically equal to", "amssymb"],
  ["\\risingdotseq", "≓", "image of or approximately equal", "amssymb"],
  ["\\fallingdotseq", "≒", "approximately equal or image of", "amssymb"],
  ["\\backsim", "∽", "reversed tilde", "amssymb"],
  ["\\backsimeq", "⋍", "reversed tilde equals", "amssymb"],
  ["\\thicksim", "∼", "thick similar", "amssymb"],
  ["\\thickapprox", "≈", "thick approximately equal", "amssymb"],
  ["\\eqcirc", "≖", "ring in equal to", "amssymb"],
  ["\\circeq", "≗", "ring equal to", "amssymb"],
  ["\\triangleq", "≜", "delta equal to, defined as", "amssymb"],
  ["\\bumpeq", "≏", "difference between", "amssymb"],
  ["\\Bumpeq", "≎", "geometrically equivalent", "amssymb"],
  ["\\eqsim", "≂", "minus tilde", "amssymb"],
  ["\\subseteqq", "⫅", "subset of above equals", "amssymb"],
  ["\\supseteqq", "⫆", "superset of above equals", "amssymb"],
  ["\\Subset", "⋐", "double subset", "amssymb"],
  ["\\Supset", "⋑", "double superset", "amssymb"],
  ["\\preccurlyeq", "≼", "precedes or equal (curly)", "amssymb"],
  ["\\succcurlyeq", "≽", "succeeds or equal (curly)", "amssymb"],
  ["\\curlyeqprec", "⋞", "equal or precedes", "amssymb"],
  ["\\curlyeqsucc", "⋟", "equal or succeeds", "amssymb"],
  ["\\precsim", "≾", "precedes or equivalent", "amssymb"],
  ["\\succsim", "≿", "succeeds or equivalent", "amssymb"],
  ["\\precapprox", "⪷", "precedes or approximately", "amssymb"],
  ["\\succapprox", "⪸", "succeeds or approximately", "amssymb"],
  ["\\vartriangleleft", "⊲", "normal subgroup of", "amssymb"],
  ["\\vartriangleright", "⊳", "contains as normal subgroup", "amssymb"],
  ["\\trianglelefteq", "⊴", "normal subgroup of or equal", "amssymb"],
  ["\\trianglerighteq", "⊵", "contains as normal subgroup or equal", "amssymb"],
  ["\\vDash", "⊨", "true, double turnstile", "amssymb"],
  ["\\Vdash", "⊩", "forces", "amssymb"],
  ["\\Vvdash", "⊪", "triple vertical bar turnstile", "amssymb"],
  ["\\shortmid", "∣", "short divides", "amssymb"],
  ["\\shortparallel", "∥", "short parallel", "amssymb"],
  ["\\smallsmile", "⌣", "small smile", "amssymb"],
  ["\\smallfrown", "⌢", "small frown", "amssymb"],
  ["\\between", "≬", "between", "amssymb"],
  ["\\pitchfork", "⋔", "pitchfork, transverse", "amssymb"],
  ["\\varpropto", "∝", "proportional to (variant)", "amssymb"],
  ["\\backepsilon", "∍", "small contains, reversed epsilon", "amssymb"],
  ["\\blacktriangleleft", "◀", "black left triangle", "amssymb"],
  ["\\blacktriangleright", "▶", "black right triangle", "amssymb"],
  ["\\coloneqq", "≔", "colon equals, is defined as, assignment", "mathtools"],
  ["\\eqqcolon", "≕", "equals colon", "mathtools"],
];

const NEGATED: readonly Row[] = [
  ["\\neq", "≠", "not equal to"],
  ["\\ne", "≠", "not equal to (short form)"],
  ["\\notin", "∉", "not an element of"],
  ["\\nless", "≮", "not less than", "amssymb"],
  ["\\ngtr", "≯", "not greater than", "amssymb"],
  ["\\nleq", "≰", "neither less than nor equal to", "amssymb"],
  ["\\ngeq", "≱", "neither greater than nor equal to", "amssymb"],
  ["\\lneq", "⪇", "less than and single-line not equal", "amssymb"],
  ["\\gneq", "⪈", "greater than and single-line not equal", "amssymb"],
  ["\\lneqq", "≨", "less than but not equal", "amssymb"],
  ["\\gneqq", "≩", "greater than but not equal", "amssymb"],
  ["\\lnsim", "⋦", "less than but not equivalent", "amssymb"],
  ["\\gnsim", "⋧", "greater than but not equivalent", "amssymb"],
  ["\\lnapprox", "⪉", "less than and not approximate", "amssymb"],
  ["\\gnapprox", "⪊", "greater than and not approximate", "amssymb"],
  ["\\nprec", "⊀", "does not precede", "amssymb"],
  ["\\nsucc", "⊁", "does not succeed", "amssymb"],
  ["\\npreceq", "⋠", "does not precede or equal", "amssymb"],
  ["\\nsucceq", "⋡", "does not succeed or equal", "amssymb"],
  ["\\precneqq", "⪵", "precedes above not equal", "amssymb"],
  ["\\succneqq", "⪶", "succeeds above not equal", "amssymb"],
  ["\\precnsim", "⋨", "precedes but not equivalent", "amssymb"],
  ["\\succnsim", "⋩", "succeeds but not equivalent", "amssymb"],
  ["\\precnapprox", "⪹", "precedes above not approximately", "amssymb"],
  ["\\succnapprox", "⪺", "succeeds above not approximately", "amssymb"],
  ["\\nsim", "≁", "not similar, not tilde", "amssymb"],
  ["\\ncong", "≆", "approximately but not actually equal", "amssymb"],
  ["\\nmid", "∤", "does not divide", "amssymb"],
  ["\\nparallel", "∦", "not parallel", "amssymb"],
  ["\\nvdash", "⊬", "does not prove", "amssymb"],
  ["\\nvDash", "⊭", "not true", "amssymb"],
  ["\\nVdash", "⊮", "does not force", "amssymb"],
  [
    "\\nVDash",
    "⊯",
    "negated double vertical bar double right turnstile",
    "amssymb",
  ],
  ["\\ntriangleleft", "⋪", "not normal subgroup of", "amssymb"],
  ["\\ntriangleright", "⋫", "does not contain as normal subgroup", "amssymb"],
  ["\\ntrianglelefteq", "⋬", "not normal subgroup of or equal", "amssymb"],
  [
    "\\ntrianglerighteq",
    "⋭",
    "does not contain as normal subgroup or equal",
    "amssymb",
  ],
  ["\\nsubseteq", "⊈", "neither a subset of nor equal to", "amssymb"],
  ["\\nsupseteq", "⊉", "neither a superset of nor equal to", "amssymb"],
  ["\\subsetneq", "⊊", "subset of with not equal, proper subset", "amssymb"],
  [
    "\\supsetneq",
    "⊋",
    "superset of with not equal, proper superset",
    "amssymb",
  ],
  ["\\subsetneqq", "⫋", "subset of above not equal to", "amssymb"],
  ["\\supsetneqq", "⫌", "superset of above not equal to", "amssymb"],
];

const ARROW: readonly Row[] = [
  ["\\leftarrow", "←", "left arrow"],
  ["\\gets", "←", "left arrow, gets, assignment"],
  ["\\rightarrow", "→", "right arrow"],
  ["\\to", "→", "right arrow, to, tends to, maps to"],
  ["\\uparrow", "↑", "up arrow"],
  ["\\downarrow", "↓", "down arrow"],
  ["\\leftrightarrow", "↔", "left right arrow"],
  ["\\updownarrow", "↕", "up down arrow"],
  ["\\Leftarrow", "⇐", "double left arrow, is implied by"],
  ["\\Rightarrow", "⇒", "double right arrow, implies"],
  ["\\Uparrow", "⇑", "double up arrow"],
  ["\\Downarrow", "⇓", "double down arrow"],
  ["\\Leftrightarrow", "⇔", "double left right arrow, if and only if"],
  ["\\Updownarrow", "⇕", "double up down arrow"],
  ["\\longleftarrow", "⟵", "long left arrow"],
  ["\\longrightarrow", "⟶", "long right arrow"],
  ["\\longleftrightarrow", "⟷", "long left right arrow"],
  ["\\Longleftarrow", "⟸", "long double left arrow"],
  ["\\Longrightarrow", "⟹", "long double right arrow"],
  ["\\Longleftrightarrow", "⟺", "long double left right arrow"],
  ["\\mapsto", "↦", "maps to"],
  ["\\longmapsto", "⟼", "long maps to"],
  ["\\hookleftarrow", "↩", "left hook arrow"],
  ["\\hookrightarrow", "↪", "right hook arrow, injection, embedding"],
  ["\\leftharpoonup", "↼", "left harpoon up"],
  ["\\leftharpoondown", "↽", "left harpoon down"],
  ["\\rightharpoonup", "⇀", "right harpoon up"],
  ["\\rightharpoondown", "⇁", "right harpoon down"],
  ["\\rightleftharpoons", "⇌", "right left harpoons, equilibrium"],
  ["\\leftrightharpoons", "⇋", "left right harpoons", "amssymb"],
  ["\\nearrow", "↗", "north east arrow"],
  ["\\searrow", "↘", "south east arrow"],
  ["\\swarrow", "↙", "south west arrow"],
  ["\\nwarrow", "↖", "north west arrow"],
  ["\\leadsto", "⇝", "leads to, squiggly arrow", "amssymb"],
  ["\\rightsquigarrow", "⇝", "right squiggly arrow", "amssymb"],
  ["\\leftrightsquigarrow", "↭", "left right wave arrow", "amssymb"],
  ["\\dashleftarrow", "⇠", "dashed left arrow", "amssymb"],
  ["\\dashrightarrow", "⇢", "dashed right arrow", "amssymb"],
  ["\\leftleftarrows", "⇇", "left paired arrows", "amssymb"],
  ["\\rightrightarrows", "⇉", "right paired arrows", "amssymb"],
  ["\\leftrightarrows", "⇆", "left over right arrows", "amssymb"],
  ["\\rightleftarrows", "⇄", "right over left arrows", "amssymb"],
  ["\\upuparrows", "⇈", "up paired arrows", "amssymb"],
  ["\\downdownarrows", "⇊", "down paired arrows", "amssymb"],
  ["\\Lleftarrow", "⇚", "left triple arrow", "amssymb"],
  ["\\Rrightarrow", "⇛", "right triple arrow", "amssymb"],
  ["\\twoheadleftarrow", "↞", "left two-headed arrow", "amssymb"],
  ["\\twoheadrightarrow", "↠", "right two-headed arrow, surjection", "amssymb"],
  ["\\leftarrowtail", "↢", "left arrow with tail", "amssymb"],
  ["\\rightarrowtail", "↣", "right arrow with tail, injection", "amssymb"],
  ["\\looparrowleft", "↫", "left arrow with loop", "amssymb"],
  ["\\looparrowright", "↬", "right arrow with loop", "amssymb"],
  ["\\curvearrowleft", "↶", "anticlockwise top semicircle arrow", "amssymb"],
  ["\\curvearrowright", "↷", "clockwise top semicircle arrow", "amssymb"],
  ["\\circlearrowleft", "↺", "anticlockwise open circle arrow", "amssymb"],
  ["\\circlearrowright", "↻", "clockwise open circle arrow", "amssymb"],
  ["\\Lsh", "↰", "upwards arrow with tip left", "amssymb"],
  ["\\Rsh", "↱", "upwards arrow with tip right", "amssymb"],
  ["\\upharpoonleft", "↿", "up harpoon left", "amssymb"],
  ["\\upharpoonright", "↾", "up harpoon right, restriction", "amssymb"],
  ["\\downharpoonleft", "⇃", "down harpoon left", "amssymb"],
  ["\\downharpoonright", "⇂", "down harpoon right", "amssymb"],
  ["\\multimap", "⊸", "multimap, linear implication", "amssymb"],
  ["\\nleftarrow", "↚", "not left arrow", "amssymb"],
  ["\\nrightarrow", "↛", "not right arrow", "amssymb"],
  ["\\nLeftarrow", "⇍", "not double left arrow", "amssymb"],
  ["\\nRightarrow", "⇏", "not double right arrow, does not imply", "amssymb"],
  ["\\nleftrightarrow", "↮", "not left right arrow", "amssymb"],
  ["\\nLeftrightarrow", "⇎", "not double left right arrow", "amssymb"],
];

const LOGIC: readonly Row[] = [
  ["\\forall", "∀", "for all, universal quantifier"],
  ["\\exists", "∃", "there exists, existential quantifier"],
  ["\\nexists", "∄", "there does not exist", "amssymb"],
  ["\\neg", "¬", "logical not, negation"],
  ["\\lnot", "¬", "logical not (alternative)"],
  ["\\land", "∧", "logical and"],
  ["\\lor", "∨", "logical or"],
  ["\\implies", "⟹", "implies (long double arrow)", "amsmath"],
  ["\\impliedby", "⟸", "is implied by", "amsmath"],
  ["\\iff", "⟺", "if and only if"],
  ["\\top", "⊤", "top, true, tautology"],
  ["\\bot", "⊥", "bottom, false, contradiction"],
  ["\\therefore", "∴", "therefore", "amssymb"],
  ["\\because", "∵", "because", "amssymb"],
  ["\\in", "∈", "element of, in"],
  ["\\ni", "∋", "contains as member"],
  ["\\owns", "∋", "owns, contains as member"],
  ["\\subset", "⊂", "subset of"],
  ["\\supset", "⊃", "superset of"],
  ["\\subseteq", "⊆", "subset of or equal to"],
  ["\\supseteq", "⊇", "superset of or equal to"],
  ["\\emptyset", "∅", "empty set"],
  ["\\varnothing", "∅", "empty set (round variant)", "amssymb"],
  ["\\complement", "∁", "complement", "amssymb"],
];

const BIG: readonly Row[] = [
  ["\\sum", "∑", "summation, sigma sum"],
  ["\\prod", "∏", "product"],
  ["\\coprod", "∐", "coproduct"],
  ["\\int", "∫", "integral"],
  ["\\iint", "∬", "double integral", "amsmath"],
  ["\\iiint", "∭", "triple integral", "amsmath"],
  ["\\iiiint", "⨌", "quadruple integral", "amsmath"],
  ["\\oint", "∮", "contour integral, line integral"],
  ["\\oiint", "∯", "surface integral, closed double integral", "esint"],
  ["\\bigcap", "⋂", "big intersection, n-ary intersection"],
  ["\\bigcup", "⋃", "big union, n-ary union"],
  ["\\bigsqcup", "⨆", "big square union"],
  ["\\bigvee", "⋁", "big logical or, n-ary or"],
  ["\\bigwedge", "⋀", "big logical and, n-ary and"],
  ["\\bigodot", "⨀", "big circled dot"],
  ["\\bigoplus", "⨁", "big circled plus, direct sum"],
  ["\\bigotimes", "⨂", "big circled times, tensor product"],
  ["\\biguplus", "⨄", "big multiset union"],
];

const DELIMITER: readonly Row[] = [
  ["\\{", "{", "left brace, curly bracket"],
  ["\\}", "}", "right brace, curly bracket"],
  ["\\lbrace", "{", "left brace (command form)"],
  ["\\rbrace", "}", "right brace (command form)"],
  ["\\langle", "⟨", "left angle bracket"],
  ["\\rangle", "⟩", "right angle bracket"],
  ["\\lfloor", "⌊", "left floor"],
  ["\\rfloor", "⌋", "right floor"],
  ["\\lceil", "⌈", "left ceiling"],
  ["\\rceil", "⌉", "right ceiling"],
  ["\\vert", "∣", "vertical bar, absolute value"],
  ["\\Vert", "∥", "double vertical bar, norm"],
  ["\\|", "∥", "double vertical bar, norm (short form)"],
  ["\\lvert", "∣", "left vertical bar, absolute value", "amsmath"],
  ["\\rvert", "∣", "right vertical bar, absolute value", "amsmath"],
  ["\\lVert", "∥", "left double bar, norm", "amsmath"],
  ["\\rVert", "∥", "right double bar, norm", "amsmath"],
  ["\\backslash", "\\", "backslash, set difference"],
  ["\\ulcorner", "⌜", "upper left corner", "amssymb"],
  ["\\urcorner", "⌝", "upper right corner", "amssymb"],
  ["\\llcorner", "⌞", "lower left corner", "amssymb"],
  ["\\lrcorner", "⌟", "lower right corner", "amssymb"],
  ["\\lgroup", "⟮", "left group bracket"],
  ["\\rgroup", "⟯", "right group bracket"],
  ["\\lmoustache", "⎰", "left moustache"],
  ["\\rmoustache", "⎱", "right moustache"],
  ["\\llbracket", "⟦", "left double bracket, semantic bracket", "stmaryrd"],
  ["\\rrbracket", "⟧", "right double bracket, semantic bracket", "stmaryrd"],
];

const ACCENT: readonly Row[] = [
  ["\\hat{a}", "â", "hat, circumflex"],
  ["\\widehat{a}", "â", "wide hat (stretches over several letters)"],
  ["\\check{a}", "ǎ", "check, caron, háček"],
  ["\\tilde{a}", "ã", "tilde"],
  ["\\widetilde{a}", "ã", "wide tilde (stretches over several letters)"],
  ["\\acute{a}", "á", "acute"],
  ["\\grave{a}", "à", "grave"],
  ["\\dot{a}", "ȧ", "dot, time derivative"],
  ["\\ddot{a}", "ä", "double dot, second derivative"],
  ["\\dddot{a}", "a⃛", "triple dot", "amsmath"],
  ["\\ddddot{a}", "a⃜", "quadruple dot", "amsmath"],
  ["\\breve{a}", "ă", "breve"],
  ["\\bar{a}", "ā", "bar, macron, mean"],
  ["\\vec{a}", "a⃗", "vector arrow"],
  ["\\mathring{a}", "å", "ring"],
  ["\\overline{AB}", "A̅B̅", "overline, line segment"],
  ["\\underline{AB}", "A̲B̲", "underline"],
  ["\\overrightarrow{AB}", "AB⃗", "right arrow over, vector"],
  ["\\overleftarrow{AB}", "AB⃖", "left arrow over"],
  ["\\overbrace{x+y}", "⏞", "overbrace"],
  ["\\underbrace{x+y}", "⏟", "underbrace"],
];

const DOTS: readonly Row[] = [
  ["\\ldots", "…", "low dots, ellipsis"],
  ["\\cdots", "⋯", "centered dots, ellipsis"],
  ["\\vdots", "⋮", "vertical dots"],
  ["\\ddots", "⋱", "diagonal dots"],
  ["\\iddots", "⋰", "inverse diagonal dots", "mathdots"],
  ["\\dots", "…", "dots (adapts to context)", "amsmath"],
  ["\\dotsc", "…", "dots with commas", "amsmath"],
  ["\\dotsb", "⋯", "dots with binary operators", "amsmath"],
  ["\\dotsm", "⋯", "dots for multiplication", "amsmath"],
  ["\\dotsi", "⋯", "dots with integrals", "amsmath"],
];

const MISC: readonly Row[] = [
  ["\\infty", "∞", "infinity"],
  ["\\partial", "∂", "partial derivative"],
  ["\\nabla", "∇", "nabla, del, gradient"],
  ["\\prime", "′", "prime"],
  ["\\backprime", "‵", "reversed prime", "amssymb"],
  ["\\angle", "∠", "angle"],
  ["\\measuredangle", "∡", "measured angle", "amssymb"],
  ["\\sphericalangle", "∢", "spherical angle", "amssymb"],
  ["\\triangle", "△", "triangle"],
  ["\\triangledown", "▽", "down triangle", "amssymb"],
  ["\\blacktriangle", "▲", "black up triangle", "amssymb"],
  ["\\blacktriangledown", "▼", "black down triangle", "amssymb"],
  ["\\square", "□", "square, white square, end of proof", "amssymb"],
  ["\\Box", "□", "box, necessity", "amssymb"],
  ["\\blacksquare", "■", "black square, filled end of proof", "amssymb"],
  ["\\Diamond", "◊", "diamond, possibility", "amssymb"],
  ["\\lozenge", "◊", "lozenge", "amssymb"],
  ["\\blacklozenge", "⧫", "black lozenge", "amssymb"],
  ["\\bigstar", "★", "black star", "amssymb"],
  ["\\surd", "√", "square root sign, radical"],
  ["\\clubsuit", "♣", "club suit"],
  ["\\diamondsuit", "♢", "diamond suit"],
  ["\\heartsuit", "♡", "heart suit"],
  ["\\spadesuit", "♠", "spade suit"],
  ["\\flat", "♭", "musical flat"],
  ["\\natural", "♮", "musical natural"],
  ["\\sharp", "♯", "musical sharp"],
  ["\\checkmark", "✓", "check mark, tick", "amssymb"],
  ["\\maltese", "✠", "maltese cross", "amssymb"],
  ["\\circledS", "Ⓢ", "circled S", "amssymb"],
  ["\\circledR", "®", "registered sign (math)", "amssymb"],
  ["\\yen", "¥", "yen sign (math)", "amssymb"],
  ["\\diagup", "╱", "diagonal up", "amssymb"],
  ["\\diagdown", "╲", "diagonal down", "amssymb"],
  ["\\degree", "°", "degree sign", "gensymb"],
];

const LETTERS: readonly Row[] = [
  ["\\aleph", "ℵ", "aleph, cardinal number"],
  ["\\beth", "ℶ", "beth", "amssymb"],
  ["\\gimel", "ℷ", "gimel", "amssymb"],
  ["\\daleth", "ℸ", "daleth", "amssymb"],
  ["\\hbar", "ℏ", "h-bar, reduced Planck constant"],
  ["\\hslash", "ℏ", "h-slash, reduced Planck constant", "amssymb"],
  ["\\ell", "ℓ", "script l, ell, litre"],
  ["\\wp", "℘", "Weierstrass p"],
  ["\\Re", "ℜ", "real part (fraktur R)"],
  ["\\Im", "ℑ", "imaginary part (fraktur I)"],
  ["\\imath", "ı", "dotless i"],
  ["\\jmath", "ȷ", "dotless j"],
  ["\\eth", "ð", "eth", "amssymb"],
  ["\\mho", "℧", "mho, inverted ohm", "amssymb"],
  ["\\Finv", "Ⅎ", "turned F", "amssymb"],
  ["\\Game", "⅁", "turned sans-serif G", "amssymb"],
  ["\\mathbb{N}", "ℕ", "natural numbers (blackboard bold N)", "amssymb"],
  ["\\mathbb{Z}", "ℤ", "integers (blackboard bold Z)", "amssymb"],
  ["\\mathbb{Q}", "ℚ", "rational numbers (blackboard bold Q)", "amssymb"],
  ["\\mathbb{R}", "ℝ", "real numbers (blackboard bold R)", "amssymb"],
  ["\\mathbb{C}", "ℂ", "complex numbers (blackboard bold C)", "amssymb"],
  ["\\mathbb{P}", "ℙ", "probability, primes (blackboard bold P)", "amssymb"],
  ["\\mathbb{E}", "𝔼", "expectation (blackboard bold E)", "amssymb"],
  ["\\mathcal{L}", "ℒ", "Laplace transform, Lagrangian (calligraphic L)"],
  ["\\mathcal{O}", "𝒪", "big O notation (calligraphic O)"],
  ["\\mathcal{N}", "𝒩", "normal distribution (calligraphic N)"],
  ["\\mathcal{F}", "ℱ", "Fourier transform, sigma-algebra (calligraphic F)"],
  ["\\mathscr{L}", "ℒ", "script L", "mathrsfs"],
  ["\\mathfrak{g}", "𝔤", "Lie algebra (fraktur g)", "amssymb"],
];

const TEXT: readonly Row[] = [
  ["\\&", "&", "ampersand"],
  ["\\%", "%", "percent sign"],
  ["\\$", "$", "dollar sign"],
  ["\\#", "#", "number sign, hash"],
  ["\\_", "_", "underscore"],
  ["\\textbackslash", "\\", "backslash"],
  ["\\textasciitilde", "~", "tilde (ASCII)"],
  ["\\textasciicircum", "^", "caret, circumflex (ASCII)"],
  ["\\textless", "<", "less-than sign"],
  ["\\textgreater", ">", "greater-than sign"],
  ["\\textbar", "|", "vertical bar"],
  ["\\textbraceleft", "{", "left brace"],
  ["\\textbraceright", "}", "right brace"],
  ["\\textunderscore", "_", "underscore (command form)"],
  ["\\S", "§", "section sign"],
  ["\\P", "¶", "paragraph sign, pilcrow"],
  ["\\dag", "†", "dagger"],
  ["\\ddag", "‡", "double dagger"],
  ["\\copyright", "©", "copyright sign"],
  ["\\textregistered", "®", "registered trademark"],
  ["\\texttrademark", "™", "trademark"],
  ["\\textservicemark", "℠", "service mark", "textcomp"],
  ["\\pounds", "£", "pound sterling"],
  ["\\texteuro", "€", "euro sign", "textcomp"],
  ["\\textcent", "¢", "cent sign", "textcomp"],
  ["\\textyen", "¥", "yen sign", "textcomp"],
  ["\\textwon", "₩", "won sign", "textcomp"],
  ["\\textnaira", "₦", "naira sign", "textcomp"],
  ["\\textpeso", "₱", "peso sign", "textcomp"],
  ["\\textlira", "₤", "lira sign", "textcomp"],
  ["\\textdong", "₫", "dong sign", "textcomp"],
  ["\\textbaht", "฿", "baht sign", "textcomp"],
  ["\\textcolonmonetary", "₡", "colon currency sign", "textcomp"],
  ["\\textcurrency", "¤", "generic currency sign", "textcomp"],
  ["\\textdegree", "°", "degree sign", "textcomp"],
  ["\\textcelsius", "℃", "degree Celsius", "textcomp"],
  ["\\textmu", "µ", "micro sign", "textcomp"],
  ["\\textohm", "Ω", "ohm sign", "textcomp"],
  ["\\textperthousand", "‰", "per mille, per thousand", "textcomp"],
  ["\\textpertenthousand", "‱", "per ten thousand, basis point", "textcomp"],
  ["\\textonehalf", "½", "one half", "textcomp"],
  ["\\textonequarter", "¼", "one quarter", "textcomp"],
  ["\\textthreequarters", "¾", "three quarters", "textcomp"],
  ["\\textonesuperior", "¹", "superscript one", "textcomp"],
  ["\\texttwosuperior", "²", "superscript two", "textcomp"],
  ["\\textthreesuperior", "³", "superscript three", "textcomp"],
  ["\\texttimes", "×", "multiplication sign (text)", "textcomp"],
  ["\\textdiv", "÷", "division sign (text)", "textcomp"],
  ["\\textpm", "±", "plus-minus (text)", "textcomp"],
  ["\\textminus", "−", "minus sign (text)", "textcomp"],
  ["\\textlnot", "¬", "not sign (text)", "textcomp"],
  ["\\textsurd", "√", "square root sign (text)", "textcomp"],
  ["\\textleftarrow", "←", "left arrow (text)", "textcomp"],
  ["\\textrightarrow", "→", "right arrow (text)", "textcomp"],
  ["\\textuparrow", "↑", "up arrow (text)", "textcomp"],
  ["\\textdownarrow", "↓", "down arrow (text)", "textcomp"],
  ["\\textbullet", "•", "bullet"],
  ["\\textperiodcentered", "·", "middle dot, interpunct"],
  ["\\textopenbullet", "◦", "white bullet", "textcomp"],
  ["\\textbigcircle", "◯", "large circle", "textcomp"],
  ["\\textasteriskcentered", "∗", "centred asterisk"],
  ["\\textellipsis", "…", "ellipsis"],
  ["\\textendash", "–", "en dash (also typed --)"],
  ["\\textemdash", "—", "em dash (also typed ---)"],
  ["\\textquoteleft", "‘", "left single quotation mark"],
  ["\\textquoteright", "’", "right single quotation mark, apostrophe"],
  ["\\textquotedblleft", "“", "left double quotation mark"],
  ["\\textquotedblright", "”", "right double quotation mark"],
  ["\\textquotesingle", "'", "straight single quote", "textcomp"],
  ["\\quotedblbase", "„", "low double quotation mark", "fontenc"],
  ["\\quotesinglbase", "‚", "low single quotation mark", "fontenc"],
  ["\\guillemotleft", "«", "left guillemet, angle quotation mark", "fontenc"],
  ["\\guillemotright", "»", "right guillemet, angle quotation mark", "fontenc"],
  ["\\guilsinglleft", "‹", "single left guillemet", "fontenc"],
  ["\\guilsinglright", "›", "single right guillemet", "fontenc"],
  ["\\textexclamdown", "¡", "inverted exclamation mark"],
  ["\\textquestiondown", "¿", "inverted question mark"],
  ["\\textinterrobang", "‽", "interrobang", "textcomp"],
  ["\\textordfeminine", "ª", "feminine ordinal indicator", "textcomp"],
  ["\\textordmasculine", "º", "masculine ordinal indicator", "textcomp"],
  ["\\textnumero", "№", "numero sign", "textcomp"],
  ["\\textrecipe", "℞", "prescription, recipe", "textcomp"],
  ["\\textreferencemark", "※", "reference mark", "textcomp"],
  ["\\textestimated", "℮", "estimated sign", "textcomp"],
  ["\\textmusicalnote", "♪", "musical note", "textcomp"],
  ["\\textbrokenbar", "¦", "broken bar", "textcomp"],
  ["\\textbardbl", "‖", "double vertical line", "textcomp"],
  ["\\textlbrackdbl", "⟦", "left double bracket", "textcomp"],
  ["\\textrbrackdbl", "⟧", "right double bracket", "textcomp"],
  ["\\textvisiblespace", "␣", "visible space"],
  ["\\textblank", "␢", "blank symbol", "textcomp"],
  ["\\ss", "ß", "German sharp s, eszett"],
  ["\\ae", "æ", "ae ligature"],
  ["\\AE", "Æ", "AE ligature"],
  ["\\oe", "œ", "oe ligature"],
  ["\\OE", "Œ", "OE ligature"],
  ["\\o", "ø", "o with stroke, slashed o"],
  ["\\O", "Ø", "O with stroke, slashed O"],
  ["\\aa", "å", "a with ring"],
  ["\\AA", "Å", "A with ring, angstrom"],
  ["\\l", "ł", "l with stroke, Polish l"],
  ["\\L", "Ł", "L with stroke, Polish L"],
  ["\\i", "ı", "dotless i (text)"],
  ["\\j", "ȷ", "dotless j (text)"],
  ["\\dh", "ð", "eth (text)", "fontenc"],
  ["\\DH", "Ð", "capital eth", "fontenc"],
  ["\\th", "þ", "thorn", "fontenc"],
  ["\\TH", "Þ", "capital thorn", "fontenc"],
  ["\\ng", "ŋ", "eng", "fontenc"],
  ["\\NG", "Ŋ", "capital eng", "fontenc"],
  ["\\dj", "đ", "d with stroke", "fontenc"],
  ["\\DJ", "Đ", "capital D with stroke", "fontenc"],
];

const TEXT_ACCENT: readonly Row[] = [
  ["\\'{e}", "é", "acute accent"],
  ["\\`{e}", "è", "grave accent"],
  ["\\^{o}", "ô", "circumflex accent"],
  ['\\"{u}', "ü", "umlaut, diaeresis"],
  ["\\~{n}", "ñ", "tilde accent"],
  ["\\={o}", "ō", "macron accent"],
  ["\\.{z}", "ż", "dot above"],
  ["\\u{a}", "ă", "breve accent"],
  ["\\v{c}", "č", "caron, háček"],
  ["\\H{o}", "ő", "double acute (Hungarian umlaut)"],
  ["\\r{a}", "å", "ring above"],
  ["\\c{c}", "ç", "cedilla"],
  ["\\k{a}", "ą", "ogonek", "fontenc"],
  ["\\d{a}", "ạ", "dot below"],
  ["\\b{a}", "a̱", "bar below, macron below"],
  ["\\t{oo}", "o͡o", "tie, double inverted breve"],
];

export const LATEX_SYMBOLS: readonly LatexSymbol[] = [
  ...group("greek", "math", "latex", GREEK),
  ...group("binary", "math", "latex", BINARY),
  ...group("relation", "math", "latex", RELATION),
  ...group("negated", "math", "latex", NEGATED),
  ...group("arrow", "math", "latex", ARROW),
  ...group("logic", "math", "latex", LOGIC),
  ...group("big", "math", "latex", BIG),
  ...group("delimiter", "math", "latex", DELIMITER),
  ...group("accent", "math", "latex", ACCENT),
  ...group("dots", "math", "latex", DOTS),
  ...group("misc", "math", "latex", MISC),
  ...group("letters", "math", "latex", LETTERS),
  ...group("text", "text", "latex", TEXT),
  ...group("textAccent", "text", "latex", TEXT_ACCENT),
];

/** U+XXXX for every code point in a string. */
export function codePoints(text: string): string {
  return Array.from(text)
    .map(
      (ch) =>
        `U+${(ch.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, "0")}`
    )
    .join(" ");
}

const RE_CODE_POINT_QUERY = /^(?:u\+?)?([0-9a-f]{4,6})$/i;
const RE_ASCII = /^[ -~]*$/;
const RE_LEADING_BACKSLASH = /^\\/;
const RE_NAME_SEPARATORS = /[\s,()-]+/;

function score(symbol: LatexSymbol, query: string, lower: string): number {
  const command = symbol.command.toLowerCase();
  const bare = command.replace(RE_LEADING_BACKSLASH, "");
  if (symbol.command === query || symbol.command === `\\${query}`) {
    return 0;
  }
  if (command === lower || bare === lower) {
    return 1;
  }
  if (bare.startsWith(lower.replace(RE_LEADING_BACKSLASH, ""))) {
    return 2;
  }
  if (symbol.name.toLowerCase().split(RE_NAME_SEPARATORS).includes(lower)) {
    return 3;
  }
  if (symbol.name.toLowerCase().includes(lower) || command.includes(lower)) {
    return 4;
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * Symbols matching a query: by command (with or without its backslash), by name, by
 * the character itself, or by code point (`2192`, `U+2192`). Best matches first.
 */
export function searchSymbols(query: string): LatexSymbol[] {
  const trimmed = query.trim();
  if (trimmed === "") {
    return [];
  }
  const codePoint = RE_CODE_POINT_QUERY.exec(trimmed);
  if (codePoint && !Number.isNaN(Number.parseInt(codePoint[1], 16))) {
    const target = `U+${codePoint[1].toUpperCase().padStart(4, "0")}`;
    const byCode = LATEX_SYMBOLS.filter((symbol) =>
      codePoints(symbol.char).split(" ").includes(target)
    );
    if (byCode.length > 0) {
      return byCode;
    }
  }
  if (!RE_ASCII.test(trimmed)) {
    return LATEX_SYMBOLS.filter((symbol) => symbol.char.includes(trimmed));
  }
  const lower = trimmed.toLowerCase();
  return LATEX_SYMBOLS.map((symbol) => ({
    symbol,
    rank: score(symbol, trimmed, lower),
  }))
    .filter((entry) => entry.rank !== Number.POSITIVE_INFINITY)
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => entry.symbol);
}
